"use client";
import { Button } from "./ui/button";
import profoundClubGroupImage from "../images/profound-club-group.png";
import { DisplayHero } from "./typography";
import dash from "../images/dash.svg";
import profoundLogo from "../images/profound-logo.svg";
import profoundWord from "../images/profound-word.svg";
import smallProfoundLogo from "../images/small-profound-logo.svg";
import smallProfoundWord from "../images/small-profound-word.svg";
import { getLinkedInAuthUrl } from "../lib/actions/linkedin";
import { getVariantConfig, type HeadlineSegment } from "../lib/config/ab-tests";
import { useABTestContext } from "../providers/ABTestProvider";

interface HeroSectionProps {
	variant?: string | null;
}

/**
 * Render a headline segment (string or formatted object)
 */
function renderHeadlineSegment(segment: HeadlineSegment, isMobile: boolean): React.ReactNode {
	if (typeof segment === "string") {
		return segment;
	}

	// It's a formatted object
	const { text, hasDash, className } = segment;
	const baseClassName = `font-editorial italic relative font-normal ${isMobile ? "text-[clamp(36px,5vw,92px)]" : "text-[clamp(24px,5vw,92px)]"} leading-[120%] tracking-[-0.08em] ${className || ""}`;

	if (hasDash) {
		return (
			<span className={baseClassName}>
				{text}
				<img
					src={dash}
					alt=""
					width={295}
					height={5}
					className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 rotate-[-1.87deg]"
					style={{
						strokeWidth: "12px",
						stroke: "#10333B",
					}}
				/>
			</span>
		);
	}

	return <span className={className || ""}>{text}</span>;
}

export default function HeroSection({ variant: variantProp }: HeroSectionProps) {
	const handlewithLinkedInClick = async () => {
		const authUrl = await getLinkedInAuthUrl();
		window.location.href = authUrl;
	};

	// Prefer context variant over prop (context reads from cookie, so it handles
	// the case where user visits without v parameter but has a valid cookie)
	const { variant: variantFromContext } = useABTestContext();
	// Use context variant if available (from cookie), otherwise fall back to prop (from URL)
	const variant = variantFromContext ?? variantProp;

	// Get variant config if variant is provided, otherwise use default content
	const variantConfig = variant ? getVariantConfig(variant) : null;
	const isDefault = !variantConfig;

	return (
		<div className="min-h-screen flex flex-col items-center justify-start bg-white text-gray-900">
			{/* Profound Logo */}
			<div className="w-full flex justify-center pt-8">
				{/* Desktop: Horizontal layout */}
				<div className="hidden md:flex items-center gap-3">
					<img
						src={profoundLogo}
						alt="Profound Logo"
						width={32}
						height={32}
					/>
					<img src={profoundWord} alt="Profound" width={120} height={32} />
				</div>

				{/* Mobile: Vertical layout */}
				<div className="flex md:hidden flex-col items-center gap-2">
					<img
						src={smallProfoundLogo}
						alt="Profound Logo"
						width={24}
						height={24}
					/>
					<img
						src={smallProfoundWord}
						alt="Profound"
						width={80}
						height={20}
					/>
				</div>
			</div>

			{/* Hero Section */}
			<main className="flex flex-col items-center text-center px-4 py-16 sm:px-6 lg:px-8 md:py-24 lg:py-32 max-w-4xl mx-auto">
				<DisplayHero className="mb-6">
					{isDefault ? (
						<>
							{/* Mobile: Three-line structure with dash */}
							<div className="block md:hidden">
								<div>It's time the world</div>
								<div>
									understood your{" "}
									<span className="font-editorial italic relative font-normal text-[clamp(36px,5vw,92px)] leading-[120%] tracking-[-0.08em]">
										career
										<img
											src={dash}
											alt=""
											width={295}
											height={5}
											className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 rotate-[-1.87deg]"
											style={{
												strokeWidth: "12px",
												stroke: "#10333B",
											}}
										/>
									</span>{" "}
									as
								</div>
								<div>well as you do.</div>
							</div>

							{/* Desktop: Original structure with dash */}
							<div className="hidden md:block">
								It's time the world understood your{" "}
								<span className="font-editorial italic relative font-normal text-[clamp(24px,5vw,92px)] leading-[120%] tracking-[-0.08em]">
									career
									<img
										src={dash}
										alt=""
										width={295}
										height={5}
										className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 rotate-[-1.87deg]"
										style={{
											strokeWidth: "12px",
											stroke: "#10333B",
										}}
									/>
								</span>{" "}
								as well as you do.
							</div>
						</>
					) : (
						<>
							{/* Mobile: Variant structure */}
							<div className="block md:hidden">
								{variantConfig.hero.headline.mobile.map((line, index) => (
									<div key={index}>
										{Array.isArray(line) ? (
											<>
												{line.map((segment, segIndex) => (
													<span key={segIndex}>
														{renderHeadlineSegment(segment, true)}
													</span>
												))}
											</>
										) : (
											renderHeadlineSegment(line, true)
										)}
									</div>
								))}
							</div>

							{/* Desktop: Variant structure */}
							<div className="hidden md:block">
								{variantConfig.hero.headline.desktop.map((segment, index) => (
									<span key={index}>{renderHeadlineSegment(segment, false)}</span>
								))}
							</div>
						</>
					)}
				</DisplayHero>
				<p className="text-center mb-8 max-w-2xl font-inter font-normal text-[clamp(16px,2vw,20px)] leading-[130%] tracking-[-0.04em] text-gray-700">
					{variantConfig?.hero.description ||
						"Meet Profound - your private AI career concierge. It listens. It understands your journey. It connects you with the right people, in the right rooms, at the right time."}
				</p>

				<Button
					className="px-8 py-3 transition-colors duration-200 flex items-center gap-1 bg-[#0274B3] text-white rounded-xl text-center font-inter text-sm not-italic font-bold leading-6 tracking-[0.28px] uppercase cursor-pointer"
					onClick={handlewithLinkedInClick}
				>
					<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
						<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
					</svg>
					<span className="ml-1">Login with LinkedIn</span>
				</Button>
				<p className="text-sm text-gray-500 mt-4">
					Private. Intelligent. Designed for outcomes, not feeds.
				</p>
			</main>

			{/* Illustration - Full Width */}
			<div className="w-full self-stretch md:px-12 md:-mt-32 max-h-[582px] z-0">
				<img
					src={profoundClubGroupImage || ""}
					alt="Profound Club Group"
					width={1200}
					height={700}
					className="w-full h-full object-contain max-h-[582px]"
				/>
			</div>
		</div>
	);
}
